import { toRaw } from 'vue';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import { restaurantId, token } from '@/refs/common.refs';
import axios from 'axios';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import type {
  IFileImage,
  IFileResponse,
  IFileUpdateBody,
} from '@/types/file.types';
import { fileId, fileSearchQuery, pageFile } from '@/refs/file.refs';

export const FileService = {
  /**
   * Téléverse (upload) une image liée à un produit ou un commerce.
   *
   * Cette méthode :
   * - Crée un FormData contenant le fichier et ses informations associées
   * - Envoie la requête d’upload vers l’API
   * - Ajoute automatiquement le token d’authentification dans les en-têtes
   * - Retourne la réponse du serveur ou une erreur en cas d’échec
   *
   * @function uploadImage
   * @param {IFileImage} data - Données nécessaires à l'upload, incluant :
   *  - file : Le fichier image à téléverser
   *  - designation : Nom ou description associée à l’image
   *  - restaurantId : Identifiant du commerce lié à l’image
   *
   * @returns {Promise<AxiosResponse | AxiosError>} Une promesse contenant la réponse du serveur
   * en cas de succès, ou une erreur rejetée en cas d’échec.
   */

  uploadImage: async (data: IFileImage) => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('designation', data.designation);
      formData.append('restaurantId', data.restaurantId);

      const accessToken = token.value?.accessToken;
      const response = await axios.post(
        `${VITE_API_URL}files/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des fichiers (images, documents, etc.) liés à un commerce
   * avec support de la pagination et des filtres par désignation.
   *
   * Cette méthode :
   * - Convertit la page courante en nombre
   * - Construit les paramètres de recherche (pagination, page, restaurantId, designation)
   * - Envoie une requête GET à l’API pour récupérer les fichiers correspondants
   * - Retourne la réponse Axios contenant les données ou rejette une erreur en cas d’échec
   *
   * @function getFiles
   * @returns {Promise<AxiosResponse<IFileResponse> | AxiosError>}
   * Une promesse contenant la réponse du serveur avec la liste des fichiers,
   * ou une erreur si la requête échoue.
   */

  getFiles: async () => {
    try {
      const page = Number(toRaw(pageFile.value));
      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());
      params.append('restau', restaurantId.value);
      params.append('designation', fileSearchQuery.value);

      const response = await axiosInstance.get<any>(
        `files/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère les informations d’un fichier ou produit à partir de son identifiant unique.
   *
   * Cette méthode :
   * - Envoie une requête GET vers l’API en utilisant l’ID du fichier
   * - Retourne les données du fichier (nom, type, métadonnées, etc.)
   * - En cas d’échec, rejette une erreur Axios
   *
   * @function getFileById
   * @param {string} fileId - Identifiant unique du fichier à récupérer
   * @returns {Promise<AxiosResponse<IApiResponseProduct> | AxiosError>}
   * Une promesse contenant la réponse du serveur avec les informations du fichier,
   * ou une erreur si la requête échoue.
   */

  getFileById: async (fileId: string) => {
    try {
      const response = await axiosInstance.get<IFileResponse>(
        `files/get/${fileId}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour les informations d’un fichier existant.
   *
   * Cette méthode :
   * - Envoie une requête PUT vers l’API avec l’ID du fichier à mettre à jour
   * - Transmet les nouvelles données du fichier (nom, description, métadonnées, etc.)
   * - Gère le type de contenu JSON dans les en-têtes
   * - Retourne la réponse Axios ou rejette une erreur en cas d’échec
   *
   * @function UpdateFileDetails
   * @param {IFileUpdateBody} data - Données à mettre à jour pour le fichier, incluant
   *  les champs modifiables tels que designation, restaurantId, etc.
   * @param {string} fileId - Identifiant unique du fichier à mettre à jour
   * @returns {Promise<AxiosResponse | AxiosError>}
   * Une promesse contenant la réponse du serveur avec les informations mises à jour
   * ou une erreur si la requête échoue.
   */

  UpdateFileDetails: async (data: IFileUpdateBody, fileI: string) => {
    try {
      const response = axiosInstance.put(`files/update/${fileId.value}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un fichier existant à partir de son identifiant unique.
   *
   * Cette méthode :
   * - Envoie une requête DELETE vers l’API avec l’ID du fichier
   * - Supprime toutes les informations associées à ce fichier (métadonnées, associations au restaurant, etc.)
   * - Gère le type de contenu JSON dans les en-têtes
   * - Retourne la réponse Axios ou rejette une erreur en cas d’échec
   *
   * @function DeleteFile
   * @param {string} fileId - Identifiant unique du fichier à supprimer
   * @returns {Promise<AxiosResponse | AxiosError>}
   * Une promesse contenant la réponse du serveur confirmant la suppression,
   * ou une erreur si la requête échoue.
   */

  DeleteFile: async (fileIdx: string) => {
    try {
      const response = axiosInstance.delete(`files/delete/${fileId.value}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Recherche les fichiers avec pagination, tri et filtre par commerce(s),
   * à partir de paramètres explicites (sans dépendre de refs globaux).
   */
  searchFilesGestion: async (params: {
    page?: number;
    pageSize?: number;
    designation?: string;
    restaurantIds?: string[];
    sortBy?: 'createdAt' | 'updatedAt';
    sortOrder?: 'ASC' | 'DESC';
  }) => {
    try {
      const qs = new URLSearchParams();
      qs.append('pagination', 'true');
      qs.append('sortBy', params.sortBy ?? 'createdAt');
      qs.append('sortOrder', params.sortOrder ?? 'DESC');
      if (params.page) qs.append('page', String(params.page));
      if (params.pageSize) qs.append('pageSize', String(params.pageSize));
      if (params.designation) qs.append('designation', params.designation);
      for (const rid of params.restaurantIds ?? []) qs.append('restau', rid);
      const response = await axiosInstance.get<any>(
        `files/search?${qs.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Téléverse une image liée à un commerce, avec identifiant de commerce explicite.
   */
  uploadImageById: async (data: {
    file: File;
    designation: string;
    restaurantId: string;
  }) => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('designation', data.designation);
      formData.append('restaurantId', data.restaurantId);
      const accessToken = token.value?.accessToken;
      const response = await axios.post(
        `${VITE_API_URL}files/upload`,
        formData,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un fichier par son identifiant explicite.
   */
  deleteFileById: async (fileIdx: string) => {
    try {
      const response = await axiosInstance.delete(`files/delete/${fileIdx}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
